/**
 * Utility function to sort product versions in descending order
 * Handles both semantic versions (e.g., "10.0", "9.8", "9.7") and versions with letters (e.g., "7.4R1")
 */

/**
 * Parses a version string into comparable parts
 * @param {string} version - Version string like "10.0", "9.8", "7.4R1"
 * @returns {object} - Object with major, minor, and patch/letter components
 */
const parseVersion = (version) => {
    if (!version) return { major: 0, minor: 0, patch: 0, letter: '' };

    // Remove "Version" prefix if present and trim
    const cleanVersion = version.replace(/^Version\s*/i, '').trim();

    // Handle versions with letters (e.g., "7.4R1")
    const letterMatch = cleanVersion.match(/^(\d+)\.(\d+)([A-Za-z]\d*)$/);
    if (letterMatch) {
        return {
            major: parseInt(letterMatch[1], 10),
            minor: parseInt(letterMatch[2], 10),
            patch: 0,
            letter: letterMatch[3],
        };
    }

    // Handle standard semantic versions (e.g., "10.0", "9.8.1")
    const parts = cleanVersion.split('.').map((part) => parseInt(part, 10) || 0);
    return {
        major: parts[0] || 0,
        minor: parts[1] || 0,
        patch: parts[2] || 0,
        letter: '',
    };
};

/**
 * Compares two version objects
 * @param {object} a - First version object
 * @param {object} b - Second version object
 * @returns {number} - Comparison result for sorting
 */
const compareVersions = (a, b) => {
    // Compare major version
    if (a.major !== b.major) {
        return b.major - a.major; // Descending order
    }

    // Compare minor version
    if (a.minor !== b.minor) {
        return b.minor - a.minor; // Descending order
    }

    // Compare patch version
    if (a.patch !== b.patch) {
        return b.patch - a.patch; // Descending order
    }

    // Handle letter versions (e.g., R1, R2)
    if (a.letter && b.letter) {
        return b.letter.localeCompare(a.letter); // Descending order
    }

    // If one has a letter and one doesn't, the one with letter comes first
    if (a.letter && !b.letter) return -1;
    if (!a.letter && b.letter) return 1;

    return 0;
};

/**
 * Sorts versions in an updates array based on the version or displayVersion field
 * @param {Array} updates - Array of update objects with 'version' or 'displayVersion' property
 * @returns {Array} - Sorted array with newest versions first
 */
export const sortUpdatesDescending = (updates) => {
    if (!updates || !Array.isArray(updates)) return [];

    return [...updates].sort((a, b) => {
        // Use version field first, fallback to displayVersion
        const versionA = parseVersion(a.version || a.displayVersion);
        const versionB = parseVersion(b.version || b.displayVersion);
        return compareVersions(versionA, versionB);
    });
};

/**
 * Sorts an array of versions in descending order
 * @param {Array} versions - Array of version objects with 'name' property
 * @returns {Array} - Sorted array with newest versions first
 */
export const sortVersionsDescending = (versions) => {
    if (!versions || !Array.isArray(versions)) return [];

    return [...versions].sort((a, b) => {
        const versionA = parseVersion(a.name);
        const versionB = parseVersion(b.name);
        return compareVersions(versionA, versionB);
    });
};

/**
 * Sorts a product's versions array in descending order and also sorts updates within each version
 * @param {object} product - Product object with versions array
 * @returns {object} - Product object with sorted versions and updates
 */
export const sortProductVersions = (product) => {
    if (!product || !product.versions) return product;

    const sortedVersions = sortVersionsDescending(product.versions).map(version => {
        // If this version has updates, sort them too
        if (version.updates && Array.isArray(version.updates)) {
            return {
                ...version,
                updates: sortUpdatesDescending(version.updates)
            };
        }
        return version;
    });

    return {
        ...product,
        versions: sortedVersions,
    };
};

/**
 * Sorts versions for all products in an array
 * @param {Array} products - Array of product objects
 * @returns {Array} - Array of products with sorted versions
 */
export const sortAllProductVersions = (products) => {
    if (!products || !Array.isArray(products)) return [];

    return products.map((product) => sortProductVersions(product));
};
